import { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export type ProjectPermission =
  | 'can_edit_caption'
  | 'can_edit_story'
  | 'can_edit_clip'
  | 'can_download'
  | 'can_publish'
  | 'can_share_demo'
  | 'can_share_social_account';

/** What the signed-in user may do on a project, as returned by GET /api/projects(/:id). */
export interface ProjectAccess {
  isOwner: boolean;
  role: string;
  permissions: string[];
  shareType?: string;
  expiresAt?: string | null;
}

export const OWNER_ACCESS: ProjectAccess = { isOwner: true, role: 'Owner', permissions: [] };

/** Owners can do everything; members only what their role grants. Unknown access = owner (legacy payloads). */
export function can(access: ProjectAccess | null | undefined, permission: ProjectPermission): boolean {
  if (!access || access.isOwner) return true;
  return access.permissions.includes(permission);
}

export const isOwnerAccess = (access: ProjectAccess | null | undefined): boolean => !access || access.isOwner;

/** Loads the caller's access for one project. `access` is null until known. UX only — the API enforces it. */
export function useProjectAccess(projectId: string | undefined): { access: ProjectAccess | null; loading: boolean } {
  const [access, setAccess] = useState<ProjectAccess | null>(null);
  const [loading, setLoading] = useState(!!projectId);

  useEffect(() => {
    if (!projectId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    fetch(`${API_BASE}/api/projects/${projectId}`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(p => { if (!cancelled) setAccess(p?.access ?? null); })
      .catch(() => { if (!cancelled) setAccess(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId]);

  return { access, loading };
}
